import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type EGButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'destructive-outline';
export type EGButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'pill';

const egButtonVariants = cva(
  'rounded-lg text-center text-sm shadow-none focus-visible:outline-offset-1 transition-colors duration-300 whitespace-nowrap w-fit cursor-pointer disabled:cursor-default',
  {
    variants: {
      egVariant: {
        'primary': [
          'bg-primary',
          'text-white',
          'active:outline-none',
          'active:bg-primary-dark',
          'disabled:bg-neutral-200',
          'disabled:text-neutral-400',
          'focus-visible:outline-primary',
          'hover:bg-primary-dark',
        ],
        'secondary': [
          'bg-white',
          'text-body',
          'border',
          'border-neutral-200',
          'active:outline-none',
          'active:bg-transparent',
          'active:border-transparent',
          'disabled:bg-neutral-200',
          'disabled:text-neutral-400',
          'focus-visible:outline-primary',
          'hover:bg-background-dark-grey',
        ],
        'ghost': [
          'bg-transparent',
          'text-body',
          'border-none',
          'active:bg-transparent',
          'active:text-body',
          'active:border-none',
          'active:no-underline',
          'active:outline-none',
          'disabled:bg-neutral-200',
          'disabled:text-neutral-400',
          'focus-visible:outline-primary',
          'focus-visible:bg-white',
          'focus-visible:border-none',
          'hover:text-primary',
          'hover:underline',
          'hover:underline-offset-2',
          'hover:bg-transparent',
        ],
        'destructive': [
          'bg-alert-danger-muted',
          'text-alert-danger',
          'active:outline-none',
          'active:bg-[#F6C0BC]',
          'active:text-alert-danger-dark',
          'disabled:bg-neutral-200',
          'disabled:text-zinc-500',
          'focus-visible:outline-alert-danger',
          'hover:bg-red-100',
          'hover:text-alert-danger-dark',
        ],
        'destructive-outline': [
          'bg-transparent',
          'text-alert-danger',
          'border',
          'border-alert-danger',
          'active:outline-none',
          'active:bg-red-50',
          'active:text-alert-danger-dark',
          'disabled:bg-transparent',
          'disabled:text-zinc-400',
          'disabled:border-zinc-300',
          'focus-visible:outline-alert-danger',
          'hover:bg-red-50',
          'hover:text-alert-danger-dark',
          'hover:border-alert-danger-dark',
        ],
      },
      egSize: {
        xs: ['px-3', 'py-2'],
        sm: ['px-6', 'py-3'],
        md: ['px-6', 'py-4', 'font-medium'],
        lg: ['p-6', 'text-base', 'font-semibold'],
        pill: ['px-[16px]', 'py-[6px]', 'text-sm', 'rounded-[24px]'],
      },
    },
    defaultVariants: {
      egVariant: 'primary',
      egSize: 'md',
    },
  },
);

interface EGButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>,
    VariantProps<typeof egButtonVariants> {
  label?: string;
  icon?: React.ReactNode;
  iconRight?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export const EGButton = forwardRef<HTMLButtonElement, EGButtonProps>(
  (
    {
      egVariant = 'primary',
      egSize = 'md',
      label = '',
      icon,
      iconRight = true,
      disabled = false,
      loading = false,
      type,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(egButtonVariants({ egVariant, egSize }), className)}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" />}
        {!iconRight && !loading && icon}
        {label || children}
        {iconRight && !loading && icon}
      </Button>
    );
  },
);

EGButton.displayName = 'EGButton';

export default EGButton;
